/*
 * useCreateVersionWithExtraction - Hook for creating new version with AI extraction
 *
 * FUNCTIONALITY:
 * - Upload PDF/image files for AI processing
 * - Create new draft version with extracted rules
 * - Handle extraction errors and loading states
 * - Return new version ID and extracted rules
 */

import useMutationApi from 'src/api/useMutationApi';
import { SupportedModelId } from 'src/types/aiModels';

// Import shared types
import type { RebateRule } from '@erp/shared-types';

interface CreateVersionWithExtractionRequest {
  files: File[];
  model: SupportedModelId;
}

interface CreateVersionWithExtractionResponse {
  newVersionId: string;
  extractedRules: RebateRule[];
  versionNumber: number;
}

interface CreateVersionWithExtractionError {
  message: string;
  code?: string;
  details?: unknown;
}

export const useCreateVersionWithExtraction = (versionGroupId: string) => {
  return useMutationApi<
    CreateVersionWithExtractionRequest,
    CreateVersionWithExtractionResponse
  >({
    endpoint: `rebates/agreements/${versionGroupId}/versions`,
    method: 'POST',
    transformRequest: ({
      files,
      model,
    }: CreateVersionWithExtractionRequest) => {
      const formData = new FormData();
      files.forEach((file: File) => {
        formData.append('files', file);
      });
      formData.append('model', model);
      return formData;
    },
  });
};

/*
 * useExtractRulesOnly - Hook for extracting rules without creating version
 *
 * FUNCTIONALITY:
 * - Upload PDF/image files for AI processing
 * - Extract rules and product groups in memory
 * - Return extracted data without persisting
 * - Used for preview before version creation
 */

interface ExtractRulesOnlyRequest {
  versionGroupId: string;
  files: File[];
  model: SupportedModelId;
}

interface ProductGroupWithStatus {
  id: string;
  name: string;
  description: string;
  supplierId: string;
  isNew: boolean;
  isDeleted: boolean;
}

export interface ExtractRulesOnlyResponse {
  extractedRules: RebateRule[];
  productGroups: {
    matched: ProductGroupWithStatus[];
    new: ProductGroupWithStatus[];
    all: ProductGroupWithStatus[];
  };
  extractionMetadata: {
    processingTime: number;
    confidence: number;
    model: string;
  };
}

export const useExtractRulesOnly = () => {
  return useMutationApi<ExtractRulesOnlyRequest, ExtractRulesOnlyResponse>({
    endpoint: 'rebates/agreements/extract-rules',
    method: 'POST',
    transformRequest: ({
      versionGroupId,
      files,
      model,
    }: ExtractRulesOnlyRequest) => {
      const formData = new FormData();
      files.forEach((file: File) => {
        formData.append('files', file);
      });
      formData.append('versionGroupId', versionGroupId);
      formData.append('model', model);
      return formData;
    },
  });
};

/*
 * useCreateVersionDraft - Hook for creating draft version with extracted + restored rules
 *
 * FUNCTIONALITY:
 * - Create new draft version in database
 * - Add extracted rules (from AI)
 * - Restore selected rules (with their allocations and manual payments)
 * - Copy tags and notes from previous version
 * - Returns new version ID
 */

interface CreateVersionDraftRequest {
  previousVersionId: string;
  extractedRules: ExtractRulesOnlyResponse['extractedRules'];
  restoredRuleIds: string[];
  files?: File[];
}

interface CreateVersionDraftResponse {
  newVersionId: string;
  versionNumber: number;
  restoredRules: number;
  createdRules: number;
}

export const useCreateVersionDraft = (previousVersionId: string) => {
  return useMutationApi<
    Omit<CreateVersionDraftRequest, 'previousVersionId'>,
    CreateVersionDraftResponse
  >({
    endpoint: `rebates/agreements/${previousVersionId}/create-version-draft`,
    method: 'POST',
    transformRequest: ({
      extractedRules,
      restoredRuleIds,
      files,
    }: Omit<CreateVersionDraftRequest, 'previousVersionId'>) => {
      const formData = new FormData();

      // Attach files if provided
      if (files && files.length > 0) {
        files.forEach((file: File) => {
          formData.append('files', file);
        });
      }

      // Append JSON fields
      formData.append('extractedRules', JSON.stringify(extractedRules));
      formData.append('restoredRuleIds', JSON.stringify(restoredRuleIds));

      return formData;
    },
  });
};

/*
 * useVersionMigrationAnalysis - Hook for analyzing CSV migration compatibility
 *
 * FUNCTIONALITY:
 * - Analyze which CSVs can be migrated
 * - Calculate allocation and manual payment impacts
 * - Return migration preview data
 */

interface VersionMigrationAnalysisRequest {
  versionGroupId: string;
  newVersionId: string;
  restoredRuleIds: string[];
}

interface VersionMigrationAnalysisResponse {
  compatibleInvoices: Array<{
    id: string;
    date: string;
    amount: number;
    lineItems: number;
  }>;
  incompatibleInvoices: Array<{
    id: string;
    date: string;
    amount: number;
    missingProductGroups: string[];
  }>;
  preservedAllocations: number;
  removedAllocations: number;
  preservedManualPayments: number;
  removedManualPayments: number;
}

export const useVersionMigrationAnalysis = () => {
  return useMutationApi<
    VersionMigrationAnalysisRequest,
    VersionMigrationAnalysisResponse
  >({
    endpoint:
      'rebates/agreements/:versionGroupId/versions/:newVersionId/analyze-migration',
    method: 'POST',
  });
};

/*
 * useAnalyzeVersionPreview - Hook for analyzing version impact before creation
 *
 * FUNCTIONALITY:
 * - Analyze impact of in-memory rule changes (extracted + restored)
 * - Calculate invoice, payment, and manual payment impacts
 * - Return delta analysis result for preview
 * - Used in Step 3 of version creation wizard
 */

interface AnalyzeVersionPreviewRequest {
  extractedRules: RebateRule[];
  restoredRuleIds: string[];
}

interface AnalyzeVersionPreviewResponse {
  activeProductGroupsDelta: {
    removed: string[];
    added: string[];
    unchanged: string[];
  };
  invoiceImpact: {
    totalInvoices: number;
    compatibleInvoices: number;
    incompatibleInvoices: number;
    incompatibleDetails: Array<{
      invoiceId: string;
      invoiceDate: string;
      affectedLineItems: number;
      missingProductGroups: string[];
    }>;
  };
  paymentImpact: {
    totalPayments: number;
    affectedAllocations: number;
    preservedAllocations: number;
    affectedAmount: number;
    unallocatableDetails: Array<{
      paymentId: string;
      paymentDate: string;
      paymentAmount: number;
      affectedRules: string[];
      allocationAmount: number;
    }>;
    deletedRules: string[];
  };
  manualAmountImpact: {
    totalManualAmounts: number;
    affectedManualAmounts: number;
    affectedTotal: number;
    manualAmountDetails: Array<{
      manualAmountId: string;
      divisionName: string;
      amount: number;
      date: string;
      description: string | null;
      ruleName: string;
    }>;
    deletedRules: string[];
  };
  calculationImpact: {
    currentEstimate: number;
    newEstimate: number;
    delta: number;
  };
  exclusiveRuleWarning?: {
    hasExclusiveRules: boolean;
    affectedRules: Array<{
      ruleName: string;
      qualifyingIsExclusive: boolean;
      affectedIsExclusive: boolean;
      currentExcludedGroups: string[];
    }>;
    addedProductGroups: string[];
    warningMessage: string;
  };
  severity: 'none' | 'minor' | 'major' | 'destructive';
}

export const useAnalyzeVersionPreview = (previousVersionId: string) => {
  return useMutationApi<
    Omit<AnalyzeVersionPreviewRequest, 'previousVersionId'>,
    AnalyzeVersionPreviewResponse
  >({
    endpoint: `rebates/agreements/${previousVersionId}/analyze-version-preview`,
    method: 'POST',
  });
};
